import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, CalendarDays } from "lucide-react";
import api from "../api";
import { User } from "../types/user";
import { useAuth } from "../contexts/AuthContext";
import { useStartOfWeek } from "../hooks/useStartOfWeek";
import { Button } from "./ui/Button";
import { Card, CardContent } from "./ui/Card";
import { Progress } from "./ui/Progress";
import { LoadingOverlay } from "./ui/LoadingOverlay";
import { LoadingSpinner } from "./ui/LoadingSpinner";
import StarterCollections from "./StarterCollections";
import {
  WelcomeIllustration,
  ImportIllustration,
  PlanIllustration,
  ListIllustration,
  CalendarDaysIllustration,
} from "./illustrations";
import { toast } from "./ui/Toast";
import { getWeekStartString, getDateRange } from "../utils/dateUtils";
import { getErrorMessage } from "../api";

interface OnboardingProps {
  user: User;
  onComplete: () => void;
}

interface OnboardingStep {
  title: string;
  content: React.ReactNode;
  illustration: React.ReactNode;
}

const Onboarding: React.FC<OnboardingProps> = ({ user, onComplete }) => {
  const { limits, refreshLimits } = useAuth();
  const startOfWeek = useStartOfWeek();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [planProgress, setPlanProgress] = useState<"planning" | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [totalMeals, setTotalMeals] = useState<number | null>(null);
  const navigate = useNavigate();

  const effectiveTotalMeals = totalMeals ?? limits?.total_meals ?? 0;
  const canPlanFirstWeek = effectiveTotalMeals >= 7;

  useEffect(() => {
    if (limits?.total_meals !== undefined) {
      setTotalMeals(limits.total_meals);
    }
  }, [limits?.total_meals]);

  const bumpTotalMeals = (added: number) => {
    setTotalMeals((prev) => {
      const base = prev ?? limits?.total_meals ?? 0;
      return base + added;
    });
  };

  const handlePlanMyFirstWeek = async () => {
    setPlanning(true);
    setPlanProgress("planning");
    setPlanError(null);
    try {
      const response = await api.post("/meal-plans/randomize", {
        week_start: getWeekStartString(new Date(), startOfWeek),
        days: getDateRange(getWeekStartString(new Date(), startOfWeek)),
      });
      await refreshLimits();
      toast.success("Your first week is planned", {
        description: "We've filled your week with meals from your collection.",
      });
      if (response.data.warnings && response.data.warnings.length > 0) {
        response.data.warnings.forEach((warning: string) => {
          toast.warning("Planner note", { description: warning });
        });
      }
      // Mark onboarding as complete on the server and in local state so
      // the tour does not restart after the redirect.
      try {
        await api.post("/auth/onboarding/complete");
      } catch (error) {
        console.error("Failed to mark onboarding complete:", error);
      }
      onComplete();
      // The meal-plans page was already mounted behind the onboarding
      // modal; a plain client-side navigate would not refetch the plan.
      // Force a full reload so the calendar renders the freshly created
      // week.
      window.location.assign(
        `/meal-plans?week_start=${response.data.week_start.split("T")[0]}`,
      );
    } catch (error) {
      setPlanError(getErrorMessage(error));
      setPlanProgress(null);
      toast.error("Couldn't plan your first week", {
        description: getErrorMessage(error),
      });
    } finally {
      setPlanning(false);
    }
  };

  const handleAddMyMeals = () => {
    onComplete();
    navigate("/meals");
  };

  const handleClose = () => {
    onComplete();
    navigate("/");
  };

  const steps: OnboardingStep[] = [
    {
      title: "Welcome to SevenServings!",
      content: (
        <div className="space-y-3">
          <p className="text-secondary-700">
            Hi <strong className="text-primary-600">{user.email}</strong>! We're
            excited to help you plan your meals and simplify your grocery
            shopping.
          </p>
          <p className="text-secondary-700">
            Here's a quick tour of what you can do:
          </p>
          <ul className="space-y-2 text-secondary-700">
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Create and manage your recipe collection
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Plan your weekly meals
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Auto-generate shopping lists
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Get meal suggestions with our randomiser
            </li>
          </ul>
        </div>
      ),
      illustration: <WelcomeIllustration className="h-24 w-24" />,
    },
    {
      title: "Import Starter Meals",
      content: (
        <div className="space-y-3">
          <p className="text-secondary-700">
            Kick-start your recipe collection with our curated starter packs.
            Import one (or more!) to get planning right away.
          </p>
          <div className="max-h-[300px] overflow-y-auto pr-1">
            <StarterCollections
              compact
              onImportComplete={(added) => bumpTotalMeals(added ?? 0)}
            />
          </div>
        </div>
      ),
      illustration: <ImportIllustration className="h-24 w-24" />,
    },
    {
      title: "Weekly Meal Planning",
      content: (
        <div className="space-y-3">
          <p className="text-secondary-700">
            The <strong className="text-primary-600">Meal Plans</strong> section
            helps you organise your week.
          </p>
          <p className="text-secondary-700">You can:</p>
          <ul className="space-y-2 text-secondary-700">
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Assign meals to specific days
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Set how many people you're cooking for
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Use the "Plan My Meals" button to auto-fill your week
            </li>
          </ul>
          <p className="text-secondary-700">
            The randomiser selects meals fairly, so you won't see the same meal
            too often.
          </p>
        </div>
      ),
      illustration: <PlanIllustration className="h-24 w-24" />,
    },
    {
      title: "Shopping Lists",
      content: (
        <div className="space-y-3">
          <p className="text-secondary-700">
            Once you have a meal plan, generate a{" "}
            <strong className="text-primary-600">shopping list</strong>{" "}
            automatically!
          </p>
          <p className="text-secondary-700">The app will:</p>
          <ul className="space-y-2 text-secondary-700">
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Combine ingredients from all your meals
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Scale quantities based on servings
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
              Group items for easy shopping
            </li>
          </ul>
          <p className="text-secondary-700">
            You can also mark items as purchased or add custom items.
          </p>
        </div>
      ),
      illustration: <ListIllustration className="h-24 w-24" />,
    },
    {
      title: "Plan Your First Week",
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-center">
            <CalendarDaysIllustration className="h-24 w-24" />
          </div>
          <p className="text-secondary-700">
            Ready to let SevenServings fill your week? If you have at least{" "}
            <strong className="text-primary-600">7 meals</strong> in your
            collection, we can randomise a full plan in one click.
          </p>
          {canPlanFirstWeek ? (
            <p className="text-secondary-700">
              You currently have{" "}
              <strong className="text-primary-600">
                {effectiveTotalMeals}
              </strong>{" "}
              meals — you're ready to go!
            </p>
          ) : (
            <p className="text-secondary-700">
              You currently have{" "}
              <strong className="text-primary-600">
                {effectiveTotalMeals}
              </strong>{" "}
              meal{effectiveTotalMeals === 1 ? "" : "s"}. Add a few more recipes
              (or import a starter collection) before auto-planning a full week.
            </p>
          )}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handlePlanMyFirstWeek}
              isLoading={planning && !planProgress}
              disabled={!canPlanFirstWeek || (planning && !!planProgress)}
              variant="primary"
              className="w-full"
              aria-label="Plan my first week"
            >
              <CalendarDays className="mr-2 h-4 w-4" />
              Plan My First Week
            </Button>

            {/* Progress indicator for Plan My First Week */}
            {planProgress && (
              <div
                className="space-y-2"
                role="status"
                aria-live="polite"
                data-testid="plan-first-week-progress"
              >
                <div className="flex items-center gap-2 text-sm">
                  {planProgress === "planning" ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-success-500 flex-shrink-0" />
                  )}
                  <span className="text-secondary-600">
                    Planning your week...
                  </span>
                </div>
                {planError && (
                  <p className="text-sm text-error-600" role="alert">
                    {planError}
                  </p>
                )}
              </div>
            )}
            <Button
              onClick={handleAddMyMeals}
              variant="outline"
              className="w-full"
              aria-label="Add my meals"
            >
              Add My Meals
            </Button>
            <Button
              onClick={handleClose}
              variant="ghost"
              className="w-full"
              aria-label="Close onboarding"
            >
              Close
            </Button>
          </div>
        </div>
      ),
      illustration: null,
    },
  ];

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // On the final step the CTAs handle their own navigation.
      handleClose();
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await api.post("/auth/onboarding/complete");
      onComplete();
      navigate("/");
    } catch (error) {
      console.error("Failed to skip onboarding:", error);
      onComplete();
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progressValue = (currentStep / (steps.length - 1)) * 100;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <Card className="max-w-lg w-full mx-4 relative">
        {loading && <LoadingOverlay spinnerSize="md" text="Saving..." />}
        <CardContent className="p-6">
          {/* Progress bar */}
          <div className="mb-2">
            <Progress value={progressValue} size="sm" variant="primary" />
          </div>
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentStep ? "bg-primary-500" : "bg-secondary-200"
                }`}
                data-testid={`progress-dot-${index}`}
              />
            ))}
          </div>

          {/* Illustration */}
          {steps[currentStep].illustration && (
            <div
              className="flex justify-center mb-4"
              data-testid="onboarding-illustration"
            >
              {steps[currentStep].illustration}
            </div>
          )}

          {/* Title */}
          <h2 className="text-xl font-bold text-center text-secondary-900 mb-4">
            {steps[currentStep].title}
          </h2>

          {/* Content */}
          <div className="text-secondary-700 mb-6">
            {steps[currentStep].content}
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center gap-4">
            <div>
              {currentStep > 0 && currentStep !== steps.length - 1 && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handlePrevious}
                  disabled={loading}
                >
                  Previous
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              {currentStep !== steps.length - 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSkip}
                  isLoading={loading}
                >
                  Skip Tour
                </Button>
              )}

              {currentStep !== steps.length - 1 && (
                <Button
                  type="button"
                  onClick={handleNext}
                  isLoading={loading}
                  variant="primary"
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Onboarding;
